import { z } from 'zod';
import { PointSchema } from './geojson';

/**
 * Click-fire UX contracts (Phase R.3) — the flagship detect → confirm → dispatch
 * workflow that sits on top of E.2.3 high-risk hotspots + E.2.1 active fires.
 *
 * Three pieces:
 *  1. Nearby Response Assets  (GET /api/response-assets/nearest)
 *  2. Live Aircraft           (GET /api/aircraft/nearest)
 *  3. Dispatch recommendation (POST/GET /api/dispatch-recommendations)
 *
 * The dispatch recommendation is "recommend → forward" only: it snapshots the
 * incident + the candidate resources and writes one append-only row. It NEVER
 * actually dispatches (no CAD write) and NEVER sends the email/webhook — that
 * forwarding is a stubbed Phase-2 feature, so a fresh row is always status
 * 'pending'. See Product Dev/phase-e-data-decisions.md (click-fire-hotspot UX).
 */

/* ------------------------------------------------------------------ *
 * Nearby Response Assets — GET /api/response-assets/nearest
 * ------------------------------------------------------------------ */

/** One asset near an incident, ordered by great-circle distance. */
export const NearbyAssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** asset_class (fire_station / aviation_base / wildfire_base / …). */
  type: z.string().nullable(),
  distance_km: z.number(),
  /** Server-computed 8-point compass bearing from the incident to the asset. */
  bearing: z.string(),
  location: PointSchema,
  /** response_assets.status — legacy availability flag; kept for back-compat. */
  availability_status: z.string(),
  /** R.4.2 — the IMS Resource Status Lifecycle state (available / requested /
   *  assigned / checked_in / staging / committed / demobilized). This is the
   *  source of truth the operator-facing "Nearby Response Resources" badge reads
   *  (Task #93 badge unification); availability_status stays for back-compat. */
  lifecycle_state: z.string(),
  /** Human-readable capability tags (response role, engine count, radius). */
  capabilities: z.array(z.string()),
});
export type NearbyAsset = z.infer<typeof NearbyAssetSchema>;

export const NearbyAssetsResponseSchema = z.object({
  assets: z.array(NearbyAssetSchema),
});
export type NearbyAssetsResponse = z.infer<typeof NearbyAssetsResponseSchema>;

/* ------------------------------------------------------------------ *
 * Live Aircraft — GET /api/aircraft/nearest
 * ------------------------------------------------------------------ */

/** One live ADS-B aircraft near an incident (last 30 min), ordered by distance. */
export const NearbyAircraftSchema = z.object({
  id: z.string(),
  callsign: z.string().nullable(),
  registration: z.string(),
  type: z.string(),
  distance_km: z.number(),
  altitude_ft: z.number().nullable(),
  last_seen_seconds_ago: z.number().int().nonnegative(),
  is_water_bomber: z.boolean(),
  /** True when this hull is an air-evac airlift asset — a CAF strategic/tactical
   *  lifter (CC-177, CC-130J, CH-147F) or an Op LENTUS airlift tag. Highlighted in
   *  the §4.4 evacuation logistics surface (the northern air-evac is the lead
   *  Logistics use case). Derived from aircraft_role + aircraft_type server-side. */
  is_airlift: z.boolean(),
});
export type NearbyAircraft = z.infer<typeof NearbyAircraftSchema>;

export const NearbyAircraftResponseSchema = z.object({
  aircraft: z.array(NearbyAircraftSchema),
});
export type NearbyAircraftResponse = z.infer<typeof NearbyAircraftResponseSchema>;

/* ------------------------------------------------------------------ *
 * Dispatch recommendation — POST/GET /api/dispatch-recommendations
 * ------------------------------------------------------------------ */

export const IncidentTypeSchema = z.enum(['high_risk_hotspot', 'active_fire']);
export type IncidentType = z.infer<typeof IncidentTypeSchema>;

export const RecommendationTypeSchema = z.enum(['confirm_fire', 'recommend_dispatch']);
export type RecommendationType = z.infer<typeof RecommendationTypeSchema>;

export const DispatchStatusSchema = z.enum([
  'pending',
  'acknowledged',
  'forwarded',
  'cancelled',
]);
export type DispatchStatus = z.infer<typeof DispatchStatusSchema>;

/** Client-supplied POST body. The server snapshots the rest (incident state,
 *  nearby assets/aircraft, weather) — the client only names the incident. */
export const DispatchRecommendationRequestSchema = z.object({
  incident_type: IncidentTypeSchema,
  incident_id: z.string().uuid(),
  recommendation_type: RecommendationTypeSchema,
  session_id: z.string().min(1).max(128),
  /** #122 — an explicit operator-chosen dispatch target: the id of an asset from
   *  the Nearby Response Resources list. Omitted → the server binds the nearest
   *  of-priority asset, preserving the original one-click flow. Only honoured for
   *  'recommend_dispatch' (the leg that raises a resource_request). If the id
   *  isn't among the in-range candidates the server falls back to nearest. */
  asset_id: z.string().uuid().optional(),
});
export type DispatchRecommendationRequest = z.infer<
  typeof DispatchRecommendationRequestSchema
>;

/** One asset the server recommends for this incident (top-N by relevance). */
export const RecommendedAssetSchema = z.object({
  asset_id: z.string(),
  asset_name: z.string(),
  type: z.string().nullable(),
  distance_km: z.number(),
  /** Rough straight-line ETA (distance ÷ nominal speed for the asset class). */
  eta_minutes: z.number().int().nonnegative(),
  rationale: z.string(),
});
export type RecommendedAsset = z.infer<typeof RecommendedAssetSchema>;

/** The created (or listed) dispatch recommendation row, as served to the client. */
export const DispatchRecommendationSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  incident_type: IncidentTypeSchema,
  incident_id: z.string(),
  recommendation_type: RecommendationTypeSchema,
  recommended_assets: z.array(RecommendedAssetSchema),
  status: DispatchStatusSchema,
  /** R.4.1 — id of the resource_request raised alongside a 'recommend_dispatch'
   *  (null for 'confirm_fire', which raises no request). */
  resource_request_id: z.string().nullable().optional(),
  /** Task #92 — id of the tracked IMS incident the resource_request was linked
   *  to. For an active_fire 'recommend_dispatch' the fire is auto-promoted to (or
   *  reuses) an incident so the request has an incident FK; null otherwise. The
   *  FireBody panel uses this to render its Resource Requests sub-section. */
  promoted_incident_id: z.string().nullable().optional(),
  /** Forward provenance — stamped by the "Forward to Agency" action (migration
   *  0047) when the recommendation is sent through the §4.21 delivery channel.
   *  Null on a fresh 'pending' row. */
  forwarded_at: z.string().nullable().optional(),
  forwarded_to: z.string().nullable().optional(),
  /** delivery_log.id of the forward attempt (FK by value). */
  forwarded_payload_id: z.string().nullable().optional(),
});
export type DispatchRecommendation = z.infer<typeof DispatchRecommendationSchema>;

/** Client-supplied body for POST /api/dispatch-recommendations/:id/forward.
 *  Recommend → forward is two distinct operator actions; this is the forward. */
export const DispatchForwardRequestSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** Registry destination to route to (migration 0052). Omitted → default PEOC. */
  destination_id: z.string().uuid().optional(),
});
export type DispatchForwardRequest = z.infer<typeof DispatchForwardRequestSchema>;

export const DispatchRecommendationListSchema = z.object({
  recommendations: z.array(DispatchRecommendationSchema),
});
export type DispatchRecommendationList = z.infer<
  typeof DispatchRecommendationListSchema
>;
